import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { CurrentUserDto } from '@obralink/shared';
import {
  api,
  bootstrapSession,
  getAccessToken,
  setAccessToken,
  setSessionLostHandler,
  type ApiEnvelope,
} from '@/lib/api-client';

/**
 * Sesión del usuario — tarea 2.7
 *
 * Al arrancar la aplicación se intenta una renovación silenciosa: el token de
 * acceso vive en memoria y se pierde al recargar, pero la cookie de refresco
 * sobrevive. Si funciona, el usuario entra directo; si no, va al login.
 *
 * Es lo que hace que recargar la página no expulse a nadie.
 */

interface AuthState {
  user: CurrentUserDto | null;
  status: 'checking' | 'authenticated' | 'anonymous';
  /**
   * Con segundo factor activo NO abre sesión: devuelve el desafío, y la
   * pantalla pide el código y llama a `completarMfa`.
   */
  login: (email: string, password: string) => Promise<ResultadoDeLogin>;
  completarMfa: (desafio: string, codigo: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Comprobación de permisos en la interfaz. NO es seguridad: la decide el servidor. */
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: CurrentUserDto;
}

export type ResultadoDeLogin = { mfaRequerido: false } | { mfaRequerido: true; desafio: string };

/**
 * La última ficha conocida de quien está usando la aplicación.
 *
 * SE GUARDA PARA PODER PINTAR SIN RED, no para autorizar. El token de acceso
 * sigue viviendo SÓLO en memoria —nunca aquí— porque es la credencial; esto es
 * el nombre, el rol y la lista de permisos, que es lo mismo que aparece en el
 * organigrama de la empresa y lo que hace falta para decidir qué botones se
 * pintan.
 *
 * Sin token, cualquier llamada a la API falla igual. Guardar esto no abre
 * ninguna puerta: sólo evita que recargar en un sótano te eche a la pantalla de
 * acceso con incidencias pendientes por enviar.
 */
const ULTIMA_FICHA = 'obralink:ultima-ficha';

function guardarUltimaFicha(usuario: CurrentUserDto): void {
  try {
    localStorage.setItem(ULTIMA_FICHA, JSON.stringify(usuario));
  } catch {
    /* almacenamiento lleno o bloqueado: se pierde el modo sin conexión, nada más */
  }
}

function leerUltimaFicha(): CurrentUserDto | null {
  try {
    const guardado = localStorage.getItem(ULTIMA_FICHA);
    return guardado ? (JSON.parse(guardado) as CurrentUserDto) : null;
  } catch {
    return null;
  }
}

function olvidarUltimaFicha(): void {
  try {
    localStorage.removeItem(ULTIMA_FICHA);
  } catch {
    /* nada que hacer */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('checking');

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    olvidarUltimaFicha();
    setStatus('anonymous');
  }, []);

  // Renovación silenciosa al arrancar
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // `bootstrapSession` está memorizada: si este efecto se ejecuta dos veces
      // —cosa que React hace en modo estricto— sólo se envía UNA petición de
      // renovación. Dos envíos con la misma cookie serían indistinguibles de un
      // token robado y el servidor revocaría la sesión.
      const resultado = await bootstrapSession();
      if (cancelled) return;

      // ── SIN RED NO SE EXPULSA A NADIE ────────────────────────────────────
      //
      // Es el caso que sostiene la PWA: recargar en un sótano. La sesión puede
      // seguir siendo perfectamente válida; lo único que pasa es que no se ha
      // podido preguntar. Se recupera la última ficha conocida para que la
      // aplicación se pinte y la cola de incidencias pendientes siga siendo
      // alcanzable.
      //
      // ESTO NO AUTORIZA NADA. No hay token, así que toda llamada a la API
      // falla; el servidor sigue siendo quien decide, como dice el comentario
      // de `ProtectedRoute`. Lo único que se recupera es qué pintar.
      if (resultado === 'sin-red') {
        const ultima = leerUltimaFicha();
        if (ultima) {
          setUser(ultima);
          setStatus('authenticated');
        } else {
          clearSession();
        }
        return;
      }

      if (resultado === 'rechazado' || !getAccessToken()) {
        clearSession();
        return;
      }

      try {
        const me = await api.get<ApiEnvelope<CurrentUserDto>>('/auth/me');
        if (cancelled) return;
        setUser(me.data);
        guardarUltimaFicha(me.data);
        setStatus('authenticated');
      } catch {
        if (!cancelled) clearSession();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  // Si el cliente HTTP detecta que la sesión ya no vale, se limpia el estado
  // sin esperar a que el usuario navegue.
  useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(null);
  }, [clearSession]);

  const abrirSesion = useCallback((res: LoginResponse) => {
    setAccessToken(res.accessToken);
    setUser(res.user);
    guardarUltimaFicha(res.user);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<ResultadoDeLogin> => {
      const res = await api.post<
        ApiEnvelope<LoginResponse | { mfaRequerido: true; desafio: string }>
      >('/auth/login', { email, password });

      // Con segundo factor la API no entrega ninguna sesión: sólo un desafío
      // que dura cinco minutos. Aquí NO se abre nada.
      if ('desafio' in res.data) return { mfaRequerido: true, desafio: res.data.desafio };

      abrirSesion(res.data);
      return { mfaRequerido: false };
    },
    [abrirSesion],
  );

  const completarMfa = useCallback(
    async (desafio: string, codigo: string) => {
      const res = await api.post<ApiEnvelope<LoginResponse>>('/auth/mfa/verificar', {
        desafio,
        codigo,
      });
      abrirSesion(res.data);
    },
    [abrirSesion],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      status,
      login,
      completarMfa,
      logout,
      can: (permission: string) => permissions.has(permission),
    };
  }, [user, status, login, completarMfa, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}

/**
 * El usuario en sesión, garantizado.
 *
 * Sólo se puede llamar dentro de una ruta protegida, donde el guardia ya ha
 * comprobado que existe. Evita el `user!` repartido por toda la aplicación.
 */
export function useCurrentUser(): CurrentUserDto {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser fuera de una ruta protegida');
  return user;
}
